/**
 * Competitive ban list — maintained manually since the upstream API provides
 * no ban information.
 *
 * Latest announcement: September 15, 2026 (effective September 18, 2026)
 * Sources:
 * - Riot / playriftbound.com September Ban List Updates
 * - https://riftbound.zone/en/riftbound-ban-list/
 *
 * Matching (in `src/utils/isBanned.ts`) folds champion-title punctuation
 * (comma ↔ hyphen) and strips print suffixes so catalog names
 * ("Ekko - Recurrent") match announcement names ("Ekko, Recurrent").
 *
 * Future army / deck builder: map MatchFormat `constructed` → `standard` and
 * `two-vs-two` → `constructed-2v2`, then call `isCardBannedInFormat`. Do not
 * gate the builder on format until that UI exists.
 */

/** Formats that maintain a ban list. */
export type BanFormatId = 'standard' | 'constructed-2v2';

export const BAN_FORMAT_LABELS: Record<BanFormatId, string> = {
  standard: 'Standard',
  'constructed-2v2': 'Constructed 2v2',
};

/**
 * Default formats for entries that omit `formats` — Standard Constructed bans
 * also apply in Constructed 2v2 (2v2 inherits the full Standard list, then
 * adds 2v2-only entries).
 */
export const DEFAULT_BAN_FORMATS: readonly BanFormatId[] = ['standard', 'constructed-2v2'];

export type BanCardKind = 'card' | 'battlefield';

export interface BanListEntry {
  /** Official / announcement name (comma or hyphen OK). */
  name: string;
  kind: BanCardKind;
  /** Inclusive YYYY-MM-DD when the ban takes effect. */
  effectiveFrom: string;
  /**
   * Formats where this card is banned. Defaults to {@link DEFAULT_BAN_FORMATS}
   * when omitted. Use `['constructed-2v2']` for 2v2-only bans (still legal in
   * Standard).
   */
  formats?: readonly BanFormatId[];
}

/** Date of the most recent ban-list announcement / catalog update. */
export const BAN_LIST_UPDATED = '2026-09-15';

/**
 * @deprecated Prefer {@link BAN_FORMAT_LABELS} / per-entry formats. Kept for
 * deck-validation copy until the builder learns format-specific bans.
 */
export const BAN_LIST_FORMAT = 'Standard Constructed';

/**
 * Canonical ban entries. Add new rows here; do not mutate derived sets by hand.
 *
 * Historical waves:
 * - 2026-03-31 — first Standard bans
 * - 2026-07-24 — Standard additions + 2v2 list debut (Master Yi 2v2-only)
 * - 2026-09-18 — Ekko / Stacked Deck in Standard + 2v2
 */
export const BAN_LIST_ENTRIES: readonly BanListEntry[] = [
  // —— March 31, 2026 (Standard Constructed; inherited by 2v2) ——
  { name: 'Called Shot', kind: 'card', effectiveFrom: '2026-03-31' },
  { name: 'Draven, Vanquisher', kind: 'card', effectiveFrom: '2026-03-31' },
  { name: 'Fight or Flight', kind: 'card', effectiveFrom: '2026-03-31' },
  { name: 'Scrapheap', kind: 'card', effectiveFrom: '2026-03-31' },
  { name: 'The Dreaming Tree', kind: 'battlefield', effectiveFrom: '2026-03-31' },
  { name: 'Obelisk of Power', kind: 'battlefield', effectiveFrom: '2026-03-31' },
  { name: "Reaver's Row", kind: 'battlefield', effectiveFrom: '2026-03-31' },

  // —— July 24, 2026 ——
  { name: 'Stealthy Pursuer', kind: 'card', effectiveFrom: '2026-07-24' },
  { name: "The Arena's Greatest", kind: 'battlefield', effectiveFrom: '2026-07-24' },
  { name: "Aspirant's Climb", kind: 'battlefield', effectiveFrom: '2026-07-24' },
  // 2v2-only (legal in Standard 1v1)
  {
    name: 'Master Yi, Wuju Bladesman',
    kind: 'card',
    effectiveFrom: '2026-07-24',
    formats: ['constructed-2v2'],
  },

  // —— September 18, 2026 (Standard + Constructed 2v2) ——
  {
    name: 'Ekko, Recurrent',
    kind: 'card',
    effectiveFrom: '2026-09-18',
    formats: ['standard', 'constructed-2v2'],
  },
  {
    name: 'Stacked Deck',
    kind: 'card',
    effectiveFrom: '2026-09-18',
    formats: ['standard', 'constructed-2v2'],
  },
];

export function formatsForBanEntry(entry: BanListEntry): readonly BanFormatId[] {
  return entry.formats ?? DEFAULT_BAN_FORMATS;
}

export function formatBanFormatsLabel(formats: readonly BanFormatId[]): string {
  return formats.map((id) => BAN_FORMAT_LABELS[id]).join(' / ');
}

/** Banned playable card names (all listed entries, including not-yet-effective). */
export const BANNED_CARD_NAMES: ReadonlySet<string> = new Set(
  BAN_LIST_ENTRIES.filter((e) => e.kind === 'card').map((e) => e.name),
);

/** Banned battlefield names (all listed entries, including not-yet-effective). */
export const BANNED_BATTLEFIELD_NAMES: ReadonlySet<string> = new Set(
  BAN_LIST_ENTRIES.filter((e) => e.kind === 'battlefield').map((e) => e.name),
);

/** Union of all banned names for iteration / docs. */
export const ALL_BANNED_NAMES: ReadonlySet<string> = new Set([
  ...BANNED_CARD_NAMES,
  ...BANNED_BATTLEFIELD_NAMES,
]);
